import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import './clinicalResearchSiteActivity.scss'
import { Typography, Tabs, Tab } from '@mui/material'
import { withStyles } from '@mui/styles'
import queryString from 'query-string'
import StarRating from '../../../StarRating'
import itemImage from '../../../../images/crsacDef.png'
import { Audits, Findings, QualityInvestigations, CAPAs } from '../index'
import { constants } from '../../../../lib'
import { UserContext } from '../../../context/UserContext'
import { CTErrorPage, makeNoAccessRows } from '../CTHelpers/CTErrorPage'
import { checkAccess, queryContent } from '../CTHelpers/CheckAccess'
import Loader from '../../../PageLoader'
import EmbedExternalUrl from '../../../EmbedExternalUrl'
import ContentManageButton from '../../contentManageButton'
import EmbedShoppingCart from '../../../ShoppingCart/embedShoppingCart'
import EntityUsageTabs from '../../entityUsageTabs'

// Use withStyles to build the tab other than last tab.
const StyledTab = withStyles(() => ({
  root: {
    textTransform: 'none',
    borderRight: '1px solid grey',
    fontSize: '.65rem'
  }
}))(props => <Tab disableRipple {...props} />)

// Use withStyles to build the last tab.
const LastTab = withStyles(() => ({
  root: {
    textTransform: 'none',
    fontSize: '.65rem'
  }
}))(props => <Tab disableRipple {...props} />)

// Create some style.
const contentTopStyle = {
  paddingTop: '20px'
}

const contentBottomStyle = {
  paddingBottom: '20px'
}

export const ClinicalResearchSiteActivity = ({ id }) => {
  const CTName = 'Clinical Research Site Activity'
  // Define the tabs.
  const pageTabs = { home: 0, audits: 1, findings: 2, qis: 3, capas: 4, associations: 5 }
  // Set all required state.
  // Set data to be used.
  const [data, setData] = useState()
  // Set reference entity usage/association data.
  const [usageData, setUsageData] = useState()
  // Set loading state.
  const [isLoading, setIsLoading] = useState(true)
  // Set if have loading error.
  const [hasLoadingError, setHasLoadingError] = useState(false)
  // Set if has access or not.
  const [onlyNoAccess, setOnlyNoAccess] = useState(false)
  // Set if have availability error.
  const [notAvailable, setNotAvailable] = useState()
  // Set if have null value error.
  const [hasNullValue, setHasNullValue] = useState(false)
  // Set the selected tab.
  const [selectedTab, setSelectedTab] = useState(0)
  // Fetch the user context.
  const { userRoles, userName, otherDetails } = useContext(UserContext)
  const location = useLocation()
  const history = useNavigate()
  // Get the hash for tab.
  const parsedHash = Object.keys(queryString.parse(location.hash))
  let initialTab = 0
  if (parsedHash.length && typeof (pageTabs[parsedHash[0]]) !== 'undefined') {
    initialTab = pageTabs[parsedHash[0]]
  }
  // Handle tab change.
  const handleChange = (event, newValue) => {
    if (initialTab !== newValue) {
      initialTab = newValue
      location.hash = Object.keys(pageTabs).find(key => pageTabs[key] === newValue)
      history(location)
    }
  }

  // Set selected tab based on hash.
  useEffect(() => {
    setSelectedTab(initialTab)
  }, [initialTab])

  // Check node access when ids are available.
  useEffect(() => {
    if (!id) {
      setHasNullValue(true)
      setIsLoading(false)
    } else {
      const promise = checkAccess(id)
      const show = []
      const hide = []
      promise.then(obj => {
        // Show data if have access
        if (obj.access) {
          show.push(obj.item)
        } else {
          // Hide data or generate error base on code received
          if (obj.error === 403) {
            hide.push(obj.item)
          }
          if (obj.error === 404) {
            setHasLoadingError(true)
            setIsLoading(false)
          }
        }
      }).then(() => {
        // If user have access.
        if (show && show.length > 0) {
          // Build the payload for api.
          const CRSACVariable = JSON.stringify({
            id: show[0]
          })
          // Build the query URL string & call to fetch data.
          const CRSACQuery = `${process.env.REACT_APP_TARGET_URL}/graphql?queryId=${constants.drupalQueryIds.CRSACQuery}&variables=${CRSACVariable}`
          // Query Call
          queryContent(CRSACQuery)
            .then(response => {
              // Set data or error base on response.
              if (response?.data?.nodeById?.entityLabel === null) {
                setHasLoadingError(true)
              } else {
                // Set data based on nodeQuery.
                setData(response.data)
                // Set usage/association data based on response.
                setUsageData(response.data.entityUsageContent.results[0])
              }
            })
            .catch(() => setHasLoadingError(true))
            .finally(() => setIsLoading(false))
        } else {
          if (!hide || hide.length === 0) {
            setHasLoadingError(true)
            setIsLoading(false)
          } else {
            // Generate proper user information.
            const userInfo = userName || otherDetails.name
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setOnlyNoAccess(true)
            setNotAvailable(noAccessRows)
            setIsLoading(false)
          }
        }
      })
    }
  }, [id, userName, otherDetails.name])

  // Display error message if null value received.
  if (hasNullValue && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.nullValue }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={message} />
    )
  }

  // Display error message if don't have access.
  if (onlyNoAccess && !isLoading && notAvailable) {
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={notAvailable} />
    )
  }

  // Display error message if there is some loading error.
  if (hasLoadingError && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.invalidContent }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}`} message={message} />
    )
  }

  // Show loader if data fetching in progress.
  if (isLoading) {
    // Return the loader.
    return <Loader />
  }

  // Return entire data for display purpose.
  // Tabs used:
  // - Home
  // - Audits
  // - Findings
  // - Quality Investigations
  // - CAPAs
  // - Associations
  // Display manage button based on user role.
  return !isLoading && data && (
    <div className='crsac-wrapper tab-content-wrapper'>
      <div className='crsac-company-name'>
        <img className='content-image' src={itemImage} alt={data?.nodeById.entityLabel || 'CRSA'} />
        <div display='flex' flex-direction='column'>
          <div className='crsac-title'>
            {data?.nodeById.entityLabel}
          </div>
          {data?.nodeById.starRating !== null && (<div className='crsac-star-rating'>
            <StarRating rating={data?.nodeById.starRating / 2} />
            <div className='star-rating-text'>
              {data?.nodeById.starRating / 2}/5
            </div>
          </div>)}
        </div>
        {
          userRoles.administrator ||
            userRoles.contentAuthor ||
            userRoles.feedEngineer ||
            userRoles.groupAdmin
            ? <ContentManageButton nid={id} />
            : <div />
        }
        <EmbedShoppingCart item={data.nodeById} image={itemImage} bundle="clinical_research_site_activity" />
      </div>
      <>
        <Tabs variant='fullWidth' value={selectedTab} onChange={handleChange} indicatorColor='primary'>
          <StyledTab label='Home' />
          <StyledTab label='Audits' />
          <StyledTab label='Findings' />
          <StyledTab label='Quality Investigations' />
          <StyledTab label='CAPAs' />
          <LastTab label='Associations' />
        </Tabs>
        {selectedTab === 0 &&
          <div className='content-home-wrapper'>
            <div className="content-home-info--full-column">
              <div className='content-home-info__space-apart' style={contentTopStyle}>
                <Typography className="body-embed-url" color='primary'>
                  {data.nodeById.embedExternalUrl?.uri != null ? <EmbedExternalUrl url={data.nodeById.embedExternalUrl.uri} /> : ''}
                  {data.nodeById.body?.value != null ? <div dangerouslySetInnerHTML={{ __html: data.nodeById.body?.value }} /> : ''}
                </Typography>
                <strong>
                  <Typography variant='h5' color='primary'>
                    <strong>Contacts</strong>
                  </Typography>
                </strong>
                <Typography>
                  <strong>Business Lead:</strong>
                </Typography>
                <Typography color='primary'>
                  {data.nodeById.businessLead?.entity.title}
                </Typography>
                <Typography>
                  <strong>Quality Lead:</strong>
                </Typography>
                <Typography color='primary'>
                  {data.nodeById.qualityLead?.entity.title}
                </Typography>
                <Typography>
                  <strong>Local Trial Manager:</strong>
                  <Typography color='primary'>
                    {data.nodeById.localTrialManager?.entity.title}
                  </Typography>
                </Typography>
                <Typography>
                  <strong>Principal Investigator:</strong>
                  <Typography color='primary'>
                    {data.nodeById.principalInvestigator?.entity.title}
                  </Typography>
                </Typography>
              </div>
              <div className='content-home-info__space-apart'>
                <Typography variant='h5' color='primary'>
                  <strong>Location Information</strong>
                </Typography>
                <Typography>
                  <strong>Audit Location Region:</strong>
                  <Typography color='primary'>
                    {data.nodeById.auditLocationRegion?.entity.title}
                  </Typography>
                </Typography>
                <Typography>
                  <strong>Address 1:</strong>
                  <Typography color='primary'>
                    {data?.nodeById.address1}
                  </Typography>
                </Typography>
                <Typography>
                  <strong>Address 2:</strong> <Typography color='primary'>{data?.nodeById.address2}</Typography>
                </Typography>
                <Typography>
                  <strong>City:</strong> <Typography color='primary'>{data.nodeById.city?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>State:</strong> <Typography color='primary'>{data.nodeById.state?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Country:</strong> <Typography color='primary'>{data.nodeById.countryAddress?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Country Activity Status:</strong> <Typography color='primary'>{data.nodeById.countryActivityStatus?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Latitude:</strong> <Typography color='primary'>{data?.nodeById.latitude}</Typography>
                </Typography>
                <Typography>
                  <strong>Longitude:</strong> <Typography color='primary'>{data?.nodeById.longitude}</Typography>
                </Typography>
                <Typography>
                  <strong>Region:</strong> <Typography color='primary'>{data.nodeById.region?.map(({ entity }) => entity?.title).join(', ')}</Typography>
                </Typography>
              </div>
              <div className='content-home-info__space-apart' style={contentBottomStyle}>
                <Typography variant='h5' color='primary'>
                  <strong>Additional Information</strong>
                </Typography>
                <Typography>
                  <strong>Star Rating Model:</strong> <Typography color='primary'>{data.nodeById.starRatingModel?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Compound Name:</strong> <Typography color='primary'>{data.nodeById.compoundName?.map(({ entity }) => entity.title).join(', ')}</Typography>
                </Typography>
                <Typography>
                  <strong>Deactivated Date:</strong> <Typography color='primary'>{data.nodeById.deactivated?.date}</Typography>
                </Typography>
                <Typography>
                  <strong>First Subject Enrolled Actual Date:</strong> <Typography color='primary'>{data.nodeById.firstSubjectEnrolledActualDate?.date}</Typography>
                </Typography>
                <Typography>
                  <strong>Group Association:</strong> <Typography color='primary'>{data?.nodeById.groupAssociation}</Typography>
                </Typography>
                <Typography>
                  <strong>Natural Key:</strong> <Typography color='primary'>{data?.nodeById.naturalKey}</Typography>
                </Typography>
                <Typography>
                  <strong>New Group Association:</strong> <Typography color='primary'>{data?.nodeById.newGroupAssociation}</Typography>
                </Typography>
                <Typography>
                  <strong>Phase:</strong> <Typography color='primary'>{data.nodeById.phase?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Primary Indication:</strong> <Typography color='primary'>{data.nodeById.primaryIndication?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Protocol</strong> Name: <Typography color='primary'>{data.nodeById.protocolName?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Protocol Status:</strong> <Typography color='primary'>{data.nodeById.protocolStatus?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Start Date:</strong> <Typography color='primary'>{data.nodeById.startDate?.date}</Typography>
                </Typography>
                <Typography>
                  <strong>Substance J&J Number:</strong> <Typography color='primary'>{data.nodeById.substanceJJNumber?.map(({ entity }) => entity.title).join(', ')}</Typography>
                </Typography>
                <Typography>
                  <strong>Therapeutic Area:</strong> <Typography color='primary'>{data.nodeById.therapeuticArea?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Site Name:</strong> <Typography color='primary'>{data.nodeById.siteName?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Site Id:</strong> <Typography color='primary'>{data.nodeById.siteId?.map(({ entity }) => entity.title).join(', ')}</Typography>
                </Typography>
                <Typography>
                  <strong>Site Status:</strong> <Typography color='primary'>{data.nodeById.siteStatus?.entity.title}</Typography>
                </Typography>
                <Typography>
                  <strong>Status As Of Date:</strong> <Typography color='primary'>{data.nodeById.siteStatusAsOfDate?.value}</Typography>
                </Typography>
                <Typography>
                  <strong>Site Manager:</strong> <Typography color='primary'>{data.nodeById.siteManager?.entity.title}</Typography>
                </Typography>
              </div>
            </div>
          </div>}
        {selectedTab === 1 &&
          <Audits ids={data.Audits.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 2 &&
          <Findings ids={data.Audits?.entities?.filter(n => n).map(props => props.Findings?.entities?.filter(n => n).map(({ nid }) => nid).join(',')).join(',')} />}
        {selectedTab === 3 &&
          <QualityInvestigations ids={data.QualityInvestigations.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 4 &&
          <CAPAs ids={data.CAPAs.entities?.filter(n => n).map(({ nid }) => nid).join(',')} />}
        {selectedTab === 5 &&
          <EntityUsageTabs groupUsage={data.nodeById.groups} contentUsage={usageData} />}
      </>
    </div>
  )
}
